'use-client';
import type { PropsWithChildren } from 'react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { getDataAttributes } from '@wix/editor-elements-common-utils';
import ResponsiveContainer from '@wix/thunderbolt-elements/components/ResponsiveContainer';
import MeshContainer from '@wix/thunderbolt-elements/components/MeshContainer';
import { createPortal } from 'react-dom';
import { useHamburgerMenuContext } from '../../HamburgerMenuContext';
import type { IHamburgerOverlayProps } from '../HamburgerOverlay.props';
import { dataHooks, firstFocusableElementSelector } from './constants';
import { useMasterPage } from '../../../../common/hooks/useMasterPage';
import { MenuParts } from '../../../Menu/viewer/constants';
import { useMenuContext } from '../../../Menu/viewer/MenuContext';
import { useFocusTrap } from '../../../../common/hooks/useFocusTrap/useFocusTrap';

/** Find the first genuinely scrollable descendant (overflow-y: auto|scroll). */
function findScrollContainer(root: HTMLElement): HTMLElement | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();
  while (node) {
    const el = node as HTMLElement;
    if (el.scrollHeight > el.clientHeight) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return el;
      }
    }
    node = walker.nextNode();
  }
  return null;
}

export type HamburgerOverlayCommonProps = IHamburgerOverlayProps & {
  rootClassName: string;
  overlayClassName: string;
  scrollContentClassName: string;
};

const HamburgerOverlayCommon: React.FC<HamburgerOverlayCommonProps> = props => {
  const {
    id,
    children,
    hasResponsiveLayout,
    containerProps,
    meshProps,
    hideFromDOM,
    tapOutsideToClose,
    onOpenStateChange,
    ariaLabel,
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    isMenuContainerFullscreen,
    rootClassName,
    overlayClassName,
    scrollContentClassName,
    deviceType,
    shouldResetEntranceAnimations,
  } = props;
  const {
    isMenuOpen = false,
    setIsMenuOpen,
    menuContainerRef,
    setIsMenuContainerFullscreen,
    shouldFocus,
    animationState,
    prehydrationToggleId,
  } = useHamburgerMenuContext();

  // Pre-hydration scroll restore: when the menu was open before hydration (CSS
  // checkbox), we save scrollTop during render and restore it after Thunderbolt
  // recreates the overflow-wrapper DOM during the isMenuOpen false→true transition.
  //
  // Key timing constraints:
  // - Save in render phase: useLayoutEffect is too late (React resets scrollTop in commit)
  // - Restore in RAF: must wait for all synchronous re-renders to finish
  // - Re-query DOM at restore: the overflow-wrapper element is replaced, not mutated
  // - skipFocusOnceRef: prevents useFocusTrap's auto-focus scrollIntoView override
  //
  // TODO: Replace render-phase save with useInsertionEffect when React 16 constraint is lifted.
  const prehydrationOpenRef = useRef<boolean | null>(null);
  if (prehydrationOpenRef.current === null) {
    if (prehydrationToggleId && typeof document !== 'undefined') {
      const checkbox = document.getElementById(
        prehydrationToggleId,
      ) as HTMLInputElement | null;
      prehydrationOpenRef.current = !!checkbox?.checked;
    } else {
      prehydrationOpenRef.current = false;
    }
  }
  if (isMenuOpen) {
    prehydrationOpenRef.current = false;
  }

  const savedScrollRef = useRef<number | null>(null);
  const skipFocusOnceRef = useRef(false);

  if (savedScrollRef.current === null && prehydrationOpenRef.current) {
    if (typeof document !== 'undefined') {
      const overlayEl = document.getElementById(id);
      if (overlayEl) {
        const scrollEl = findScrollContainer(overlayEl);
        if (scrollEl && scrollEl.scrollTop > 0) {
          savedScrollRef.current = scrollEl.scrollTop;
        }
      }
    }
  }

  useLayoutEffect(() => {
    if (savedScrollRef.current === null) {
      return;
    }

    if (!isMenuOpen) {
      // Hydration phase: React's commit just reset scrollTop to 0, but the
      // same DOM element still exists. Restore synchronously before paint.
      const overlayEl = document.getElementById(id);
      if (!overlayEl) {
        return;
      }
      let restored = false;
      const scrollEl = findScrollContainer(overlayEl);
      if (scrollEl) {
        scrollEl.scrollTop = savedScrollRef.current!;
        restored = scrollEl.scrollTop > 0;
      }
      if (!restored) {
        // Submenu collapsed during hydration (CSS checkbox expansion state
        // lost), so no element is scrollable and we can't restore scrollTop.
        // Hide overlay to prevent showing scrollTop=0; the isMenuOpen
        // transition branch will restore scroll via RAF once the DOM is
        // recreated.
        overlayEl.style.setProperty('visibility', 'hidden', 'important');
      }
      return;
    }

    // isMenuOpen transition: the overflow-wrapper is recreated by Thunderbolt
    // (via setIsMenuContainerFullscreen useEffect). Hide overlay to prevent a
    // brief flash of scrollTop=0 while we wait for DOM recreation, then
    // restore on the new element in RAF.
    const overlayEl = document.getElementById(id);
    if (overlayEl) {
      overlayEl.style.setProperty('visibility', 'hidden', 'important');
    }
    const savedTop = savedScrollRef.current;
    savedScrollRef.current = null;
    skipFocusOnceRef.current = true;
    requestAnimationFrame(() => {
      const overlay = document.getElementById(id);
      if (overlay) {
        const scrollEl = findScrollContainer(overlay);
        if (scrollEl) {
          scrollEl.scrollTop = savedTop;
        }
        overlay.style.removeProperty('visibility');
      }
      skipFocusOnceRef.current = false;
    });
  }, [isMenuOpen, id]);

  const { menuStyleId } = useMenuContext();

  const textLikeTypes = [
    'text',
    'search',
    'email',
    'url',
    'tel',
    'password',
    'number',
  ];

  const canTypeOnElement = (element: HTMLElement): boolean => {
    const tagName = element?.tagName;
    const elementType = (element as HTMLInputElement)?.type || 'text';
    return (
      (tagName === 'INPUT' && textLikeTypes.includes(elementType)) ||
      tagName === 'TEXTAREA' ||
      element.isContentEditable
    );
  };

  const handleFocus = (container: HTMLDivElement) => {
    if (!shouldFocus || skipFocusOnceRef.current) {
      return;
    }

    const applyFocus = () => {
      const menuContainer = menuContainerRef?.current;
      const firstSelectableElement =
        menuContainer?.querySelector<HTMLElement>(
          firstFocusableElementSelector,
        ) ?? container;

      if (firstSelectableElement) {
        const notDesktop = deviceType !== 'Desktop';
        const activeElement = document?.activeElement as HTMLElement;
        const isInputElementActive =
          activeElement && canTypeOnElement(activeElement);
        // Skip focusing if an input element is active on non-desktop devices
        if (notDesktop && isInputElementActive) {
          return;
        }
        firstSelectableElement.focus({ preventScroll: true });
      }
    };

    if (animationState.name === 'none') {
      // Timeout in order to prevent a race condition between React's rendering cycle and focus management
      setTimeout(applyFocus, 0);
    } else if (animationState.phase === 'enterDone') {
      applyFocus();
    }
  };

  const { containerRef: focusTrapRef } = useFocusTrap<HTMLDivElement>(
    isMenuOpen,
    {
      onFocus: handleFocus,
    },
  );

  const MenuStyleWrapper = useMemo(
    () =>
      menuStyleId
        ? (_props: PropsWithChildren<{}>) => (
            <div id={menuStyleId}>{_props.children}</div>
          )
        : React.Fragment,
    [menuStyleId],
  );

  const StylableBackground = (
    <div
      data-hook={dataHooks.dialog}
      aria-hidden
      className={overlayClassName}
    />
  );

  // ECL-11356: Reset entrance animations stuck after hydration remount
  useEffect(() => {
    if (!shouldResetEntranceAnimations || !isMenuOpen) {
      return;
    }

    requestAnimationFrame(() => {
      const container = focusTrapRef.current;
      if (!container) {
        return;
      }

      container.getAnimations?.({ subtree: true })?.forEach(animation => {
        if (animation.playState === 'paused') {
          animation.finish();
        }
      });
    });
  }, [shouldResetEntranceAnimations, isMenuOpen, focusTrapRef]);

  useEffect(() => {
    onOpenStateChange(isMenuOpen);
  }, [isMenuOpen, onOpenStateChange]);

  useEffect(() => {
    return () => {
      onOpenStateChange(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsMenuContainerFullscreen(isMenuContainerFullscreen);
  }, [isMenuContainerFullscreen, setIsMenuContainerFullscreen]);

  const closeMenu = useCallback(() => setIsMenuOpen(false), [setIsMenuOpen]);

  const masterPage = useMasterPage();

  const onOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      onClick?.(event);
      const target = event.target as Element | null;
      const clickedOnLink = target?.closest('a');
      const clickedOnOverlay = !target?.closest(
        `#${menuContainerRef?.current?.id}`,
      );

      if (clickedOnLink || (tapOutsideToClose && clickedOnOverlay)) {
        closeMenu();
      }
    },
    [closeMenu, menuContainerRef, onClick, tapOutsideToClose],
  );

  const childrenToRender =
    typeof children === 'function' ? children : () => children;

  const renderOverlay = () => {
    if (hideFromDOM && !isMenuOpen && !prehydrationOpenRef.current) {
      return null;
    }

    return (
      <MenuStyleWrapper>
        <div
          id={id}
          className={rootClassName}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          data-visible={isMenuOpen}
          onKeyDown={e => e.key === 'Escape' && closeMenu()}
          onClick={onOverlayClick}
          onDoubleClick={onDblClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          data-hook={dataHooks.root}
          ref={focusTrapRef}
          tabIndex={-1}
          data-part={MenuParts.HamburgerOverlay}
          data-animation-name={animationState.name}
          data-animation-state={animationState.phase}
          {...getDataAttributes(props)}
        >
          {hasResponsiveLayout ? (
            <>
              {StylableBackground}
              <ResponsiveContainer {...containerProps} tabIndex={-1}>
                {childrenToRender}
              </ResponsiveContainer>
            </>
          ) : (
            <div className={scrollContentClassName}>
              {StylableBackground}
              <MeshContainer id={id} {...meshProps}>
                {childrenToRender}
              </MeshContainer>
            </div>
          )}
        </div>
      </MenuStyleWrapper>
    );
  };

  return masterPage
    ? createPortal(renderOverlay(), masterPage)
    : renderOverlay();
};

export default HamburgerOverlayCommon;
